/** Tournament doc `status` once an admin marks it played in the builder. */
export const TOURNAMENT_STATUS_COMPLETED = 'completed'

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/

/** Today's calendar day in America/Halifax as `YYYY-MM-DD`. */
export function halifaxDateKey (now: Date = new Date()) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Halifax',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(now)
}

/** Stored `startDate` / `endDate` are bare `YYYY-MM-DD` strings (builder date inputs). */
function dateOnly (value: unknown) {
  if (typeof value !== 'string') return null
  const s = value.trim()
  return DATE_ONLY_RE.test(s) ? s : null
}

/** Last day on the ice: `endDate`, falling back to `startDate`. */
export function tournamentLastDay (t: any) {
  return dateOnly(t?.endDate) ?? dateOnly(t?.startDate)
}

/**
 * Whether a tournament is over: marked completed by an admin, or its last day
 * is before today in Halifax. Undated tournaments are never past.
 */
export function isTournamentPast (t: any, now: Date = new Date()) {
  if (!t) return false
  if (t.status === TOURNAMENT_STATUS_COMPLETED) return true
  const last = tournamentLastDay(t)
  return last != null && last < halifaxDateKey(now)
}

/**
 * Archived by a super admin (the builder's "delete"): hidden from every list and
 * public page, but registrations, payments and games are kept. Restore clears it.
 */
export function isTournamentArchived (t: any) {
  if (t == null) return false
  // `archivedBy` too: a pending serverTimestamp reads as null in the local snapshot.
  return t.archivedAt != null || (typeof t.archivedBy === 'string' && t.archivedBy !== '')
}

/**
 * Whether in-app player registration is configured (fee set, including $0)
 * and the tournament has not finished.
 * @param {{ registrationFee?: number|null }} t
 */
export function canRegisterPlayerInApp (t, now: Date = new Date()) {
  return t != null && t.registrationFee != null && !isTournamentPast(t, now)
}

/**
 * Whether in-app team registration is configured and the tournament has not finished.
 * @param {{ teamSpots?: number|null, teamRegistrationFee?: number|null }} t
 */
export function canRegisterTeamInApp (t, now: Date = new Date()) {
  if (!t) return false
  const spots = Number(t.teamSpots)
  return Number.isFinite(spots) && spots > 0 && t.teamRegistrationFee != null && !isTournamentPast(t, now)
}

/** Player and/or team registration available in the app. */
export function canRegisterInApp (t, now: Date = new Date()) {
  return canRegisterPlayerInApp(t, now) || canRegisterTeamInApp(t, now)
}

function compareByName (a: any, b: any) {
  return String(a?.name || '').localeCompare(String(b?.name || ''), undefined, { sensitivity: 'base' })
}

/**
 * Split hub tournaments into upcoming (soonest start first, undated last) and
 * past (most recently finished first). Ties fall back to name.
 */
export function partitionTournamentsByPast (list: any[], now: Date = new Date()) {
  const upcoming: any[] = []
  const past: any[] = []
  for (const t of list) (isTournamentPast(t, now) ? past : upcoming).push(t)
  upcoming.sort((a, b) => {
    const sa = dateOnly(a?.startDate) ?? tournamentLastDay(a)
    const sb = dateOnly(b?.startDate) ?? tournamentLastDay(b)
    if (sa !== sb) {
      if (sa == null) return 1
      if (sb == null) return -1
      return sa < sb ? -1 : 1
    }
    return compareByName(a, b)
  })
  past.sort((a, b) => {
    const la = tournamentLastDay(a)
    const lb = tournamentLastDay(b)
    if (la !== lb) {
      if (la == null) return 1
      if (lb == null) return -1
      return la > lb ? -1 : 1
    }
    return compareByName(a, b)
  })
  return { upcoming, past }
}
